<template>
  <div
    ref="imageContainer"
    class="image-compare"
    @mouseover="mouseOver"
    @mousemove="mouseMove"
    @touchmove="mouseMove"
    @mouseout="mouseOut"
  >
    <div class="image-compare__image-container">
      <div
        v-if="(beforeText || $slots.beforeText) && !isInline"
        class="image-compare__label image-compare__label--before"
        :class="{
          'image-compare__label--floating': !isInline,
          'image-compare__label--selected': selectedLabel === 'before'
        }"
      >
        <slot name="beforeText">
          {{ beforeText }}
        </slot>
      </div>
      <div
        v-if="(afterText || $slots.afterText) && !isInline"
        class="image-compare__label image-compare__label--after"
        :class="{
          'image-compare__label--floating': !isInline,
          hidden: hideSlider,
          'image-compare__label--selected': selectedLabel === 'after'
        }"
      >
        <slot name="afterText">
          {{ afterText }}
        </slot>
      </div>
      <div
        class="image-compare__image-before"
        :class="{
          hidden: hideSlider,
          'image-compare__transparent-background': isTransparent,
          'image-compare__transparent-background--standard-artwork': isStandardArtwork
        }"
      >
        <img
          class="image-compare__slider-image image-compare__slider-image--before"
          :src="beforeImage"
          alt="Before Image"
          @load="onBeforeImageLoad"
        />
      </div>
      <div
        class="image-compare__image-after"
        :class="{
          'image-compare__transparent-background': isTransparent,
          'image-compare__transparent-background--standard-artwork': isStandardArtwork
        }"
      >
        <img
          ref="afterImageElement"
          class="image-compare__slider-image image-compare__slider-image--after"
          :class="{
            'image-compare__slider-image--inline': isInline,
            'image-compare__slider-image--placeholder': isArtwork && !imageLoaded,
            'image-compare__slider-image--artwork': isArtwork,
            'image-compare__slider-image--full-width': showFullWidthArtwork
          }"
          :src="afterImage"
          alt="After Image"
          v-bind="$attrs"
          @load="onAfterImageLoad"
        />
        <div
          v-if="isStandardArtwork && enhancementProcessing"
          class="image-compare__enhancement-processing"
        >
          <ImageCompareLoadingContent
            v-if="experiments.ab_artwork_processing_ui === 1"
            :enhancement-tier="enhancementTier"
          />
          <div v-else class="image-compare__enhancement-processing-content">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="44"
              height="44"
              viewBox="0 0 44 44"
              fill="none"
              class="image-compare__enhancement-processing-content-icon"
            >
              <path
                d="M8.24996 40.3337V31.167M8.24996 12.8337V3.66699M3.66663 8.25033H12.8333M3.66663 35.7503H12.8333M23.8333 5.50033L20.654 13.7666C20.1369 15.1108 19.8784 15.783 19.4764 16.3483C19.1201 16.8494 18.6824 17.2872 18.1813 17.6435C17.6159 18.0455 16.9438 18.304 15.5995 18.821L7.33329 22.0003L15.5995 25.1797C16.9438 25.6967 17.6159 25.9552 18.1813 26.3572C18.6824 26.7135 19.1201 27.1513 19.4764 27.6523C19.8784 28.2177 20.1369 28.8898 20.654 30.2341L23.8333 38.5003L27.0126 30.2341C27.5296 28.8898 27.7882 28.2177 28.1902 27.6523C28.5464 27.1513 28.9842 26.7135 29.4853 26.3572C30.0507 25.9552 30.7228 25.6967 32.067 25.1797L40.3333 22.0003L32.067 18.821C30.7228 18.304 30.0507 18.0455 29.4853 17.6435C28.9842 17.2872 28.5464 16.8494 28.1902 16.3483C27.7881 15.783 27.5296 15.1108 27.0126 13.7666L23.8333 5.50033Z"
                stroke="#DD2590"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>
              30 seconds
              <br />
              to perfection!
            </span>
          </div>
        </div>
      </div>
    </div>
    <input
      v-model="mousePosition.xPerc"
      type="range"
      min="0"
      max="100"
      aria-label="Percentage of before photo shown"
      class="image-compare__slider"
      :class="{ 'image-compare__slider--disabled': hideSlider }"
    />
    <div class="image-compare__slider-lines" :class="{ hidden: hideSlider }">
      <div
        class="image-compare__slider-line image-compare__slider-line--top"
        aria-hidden="true"
      ></div>
      <div class="image-compare__slider-button" aria-hidden="true">
        <UseSvg class="image-compare__slider-button-icon" icon="arrow-left" />
        <UseSvg class="image-compare__slider-button-icon" icon="arrow-right" />
      </div>
      <div
        class="image-compare__slider-line image-compare__slider-line--bottom"
        aria-hidden="true"
      ></div>
    </div>
  </div>
  <div
    v-if="showMagnifier && isInline"
    class="image-compare__label-container"
    :class="{ hidden: hideSlider }"
  >
    <div v-if="beforeText" class="image-compare__label image-compare__label--before">
      {{ beforeText }}
    </div>
    <div v-if="afterText" class="image-compare__label image-compare__label--after">
      {{ afterText }}
    </div>
  </div>
  <div
    v-if="showMagnifier && (isHovering || isInline)"
    class="image-compare__magnifier-container"
    :class="{
      'image-compare__magnifier-container--inline': isInline,
      'image-compare__magnifier-container--right': magnifierPosition === 'right',
      'image-compare__magnifier-container--left': magnifierPosition === 'left',
      'image-compare__magnifier-container--transparent': isTransparent,
      hidden: hideSlider
    }"
  >
    <div class="image-compare__magnifier-item image-compare__magnifier-item--before"></div>
    <div class="image-compare__magnifier-item image-compare__magnifier-item--after"></div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { isTouchDevice, isMobile } from "common/utils/browser";
import { useDtfStore } from "../store";
import ImageCompareLoadingContent from "./ImageCompareLoadingContent.vue";

export default {
  components: { UseSvg, ImageCompareLoadingContent },
  props: {
    beforeImage: {
      type: String,
      default: ""
    },
    afterImage: {
      type: String,
      default: ""
    },
    beforeText: {
      type: String,
      default: undefined
    },
    afterText: {
      type: String,
      default: undefined
    },
    isInline: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isArtwork: {
      type: Boolean,
      default: false
    },
    showMagnifier: {
      type: Boolean,
      default: false
    },
    magnifierPosition: {
      type: String,
      default: "left",
      validator(value) {
        return ["left", "right"].includes(value);
      }
    },
    sliderPosition: {
      type: Number,
      default: 50
    },
    imageBackground: {
      type: String,
      default: "transparent"
    },
    enhancementProcessing: {
      type: Boolean,
      default: false
    },
    selectedLabel: {
      type: String,
      default: null
    },
    enhancementTier: {
      type: String,
      default: null
    }
  },
  emits: ["canvasLoaded"],
  data() {
    return {
      mousePosition: {
        xPerc: 50,
        yPerc: 50
      },
      isHovering: false,
      imageMaxWidth: "unset",
      imageHeight: "unset",
      disableSlider: true,
      imageLoaded: false,
      originalWidth: 0,
      originalHeight: 0,
      afterImageIntrinsicHeight: 0,
      afterImageIntrinsicWidth: 0
    };
  },
  computed: {
    ...mapState(useDtfStore, ["experiments", "isSticker", "isStickerSample", "isStandardArtwork"]),
    positionPercentageX() {
      return `${this.mousePosition.xPerc}%`;
    },
    positionPercentageY() {
      return `${this.mousePosition.yPerc}%`;
    },
    beforeImageCSS() {
      return `url(${this.beforeImage})`;
    },
    afterImageCSS() {
      return `url(${this.afterImage})`;
    },
    sliderVerticalPosition() {
      if (this.experiments.ab_artwork_processing_ui === 1) {
        return "90%";
      }

      return this.showMagnifier ? this.positionPercentageY : "50%";
    },
    imageMaxHeight() {
      return this.isArtwork ? "unset" : "80vh";
    },
    isTappable() {
      return isTouchDevice() || isMobile();
    },
    isTransparent() {
      return this.imageBackground === "transparent";
    },
    isImageSame() {
      return !this.enhancementProcessing && this.beforeImage === this.afterImage;
    },
    hideSlider() {
      return (
        this.isSticker ||
        this.isStickerSample ||
        this.disableSlider ||
        this.isImageSame ||
        !this.imageLoaded
      );
    },
    showFullWidthArtwork() {
      if (this.isFullWidth) {
        return true;
      }

      return !this.isArtwork && this.hideSlider && this.isTappable;
    }
  },
  watch: {
    sliderPosition(newValue) {
      this.mousePosition.xPerc = newValue;
    }
  },
  mounted() {
    this.mousePosition.xPerc = this.sliderPosition;
  },
  methods: {
    ...mapActions(useDtfStore, ["setPreviewCanvasSize"]),
    onBeforeImageLoad(event) {
      const image = event.target;

      this.originalWidth = image.naturalWidth;
      this.originalHeight = image.naturalHeight;

      this.disableSlider = !image.complete;

      if (this.disableSlider) return;

      this.imageMaxWidth = image.naturalWidth;

      if (this.isArtwork) return;

      if (image.naturalHeight < 1000) {
        this.imageHeight = `80vh`;
      }
    },
    onAfterImageLoad(event) {
      const image = event.target;

      // Trick to make sure the image is actually rendered on the screen.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.initializeImage(image));
      });
    },
    initializeImage(image) {
      if (!this.enhancementProcessing) {
        this.afterImageIntrinsicHeight = image.naturalHeight;
        this.afterImageIntrinsicWidth = image.naturalWidth;
      }

      if (!this.$refs.imageContainer) {
        return;
      }

      const { width, height } = this.$refs.imageContainer.getBoundingClientRect();
      this.setPreviewCanvasSize({ width, height });

      this.imageLoaded = true;
      this.$emit("canvasLoaded");
    },
    constrain(num, min = 0, max = 100) {
      return Math.min(Math.max(num, min), max);
    },
    mouseOver() {
      this.isHovering = true;
    },
    mouseOut() {
      this.isHovering = false;
    },
    getCoordinates(event) {
      if (event.type === "touchmove") {
        const { touches, changedTouches } = event.originalEvent ?? event;
        const touch = touches[0] ?? changedTouches[0];
        return { clientX: touch.clientX, clientY: touch.clientY };
      }
      return { clientX: event.clientX, clientY: event.clientY };
    },
    mouseMove(event) {
      if (this.disableSlider) return;
      requestAnimationFrame(() => {
        const { clientX, clientY } = this.getCoordinates(event);
        const { left, width, top, height } = event.target.getBoundingClientRect();
        const positionX = ((clientX - left) / width) * 100;
        const positionY = ((clientY - top) / height) * 100;
        this.mousePosition = {
          xPerc: this.constrain(positionX),
          yPerc: positionY
        };
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/settings.scss";

.image-compare {
  position: relative;
  overflow: hidden;
  touch-action: none;
  max-width: 100%;

  @include with-tablet {
    width: fit-content;
  }

  @include with-desktop {
    height: auto;
  }

  &__image-container {
    width: 100%;
    height: 100%;
  }

  &__slider-image {
    display: block;
    max-width: 100%;
    max-height: v-bind(imageMaxHeight);

    &--before {
      object-fit: cover;
      height: 100%;
      max-width: v-bind(imageMaxWidth);
    }

    &--after {
      @include with-desktop {
        height: v-bind(imageHeight);
        max-width: 970px;
      }

      @include with-large-desktop {
        max-width: 1260px;
      }
    }

    &--full-width {
      width: 90vw;
      max-width: fit-content;
      max-height: fit-content;
    }

    &--artwork {
      max-width: 100% !important;
    }

    &--inline {
      max-height: 50vh;
    }

    &--placeholder {
      width: 260px;
      height: 260px;
      visibility: hidden;

      @include with-tablet {
        width: 650px;
        height: 650px;
      }
    }
  }

  &__image-before {
    position: absolute;
    inset: 0;
    overflow: hidden;
    width: v-bind(positionPercentageX);
    background: v-bind(imageBackground);
    z-index: 1;
  }

  &__image-after {
    height: 100%;
    background: v-bind(imageBackground);
  }

  &__transparent-background {
    background: $transparent-bg;

    &--standard-artwork {
      background-image: $transparent-bg-2;
      background-color: $white;
    }
  }

  &__enhancement-processing {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &:before {
      content: "";
      display: block;
      height: 100%;
      width: 100%;
      opacity: 0.64;
      background:
        linear-gradient(0deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.64) 100%),
        url("https://jiffy.imgix.net/gen/spangle-sparkle.gif?ixlib=rb-0.3.5&auto=format") lightgray
          50% / cover no-repeat;
    }
  }

  &__enhancement-processing-content {
    position: absolute;
    width: 140px;
    border-radius: 8px;
    padding: 10px 18px;
    background: $white;
    display: flex;
    flex-flow: column;
    align-items: center;
    justify-content: center;
    color: $crafterpay-gray-modern-600;
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    gap: 10px;
  }

  &__enhancement-processing-content-icon {
    width: 28px;
    height: 28px;

    @include with-tablet {
      width: 44px;
      height: 44px;
    }
  }

  &__slider {
    position: absolute;
    inset: 0;
    cursor: pointer;
    opacity: 0;
    width: 100%;
    height: 100%;
    z-index: 4;

    &:hover {
      cursor: col-resize;
    }

    &--disabled {
      cursor: default !important;
    }
  }

  &__slider-lines {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__slider-line {
    position: absolute;
    width: 2px;
    background-color: $white;
    left: v-bind(positionPercentageX);

    &--top {
      top: 0;
      height: calc(v-bind(sliderVerticalPosition) - 22px);
      transform: translate(-1px, -1px);
    }

    &--bottom {
      height: calc(100% - v-bind(sliderVerticalPosition) - 22px);
      transform: translate(-1px, -100%);
    }
  }

  &__slider-button {
    position: absolute;
    background-color: $black-transparent;
    padding: 0.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    top: v-bind(sliderVerticalPosition);
    left: v-bind(positionPercentageX);
    transform: translate(-50%, -50%);
    pointer-events: none;
    width: 3rem;
    height: 3rem;
    border: 2px solid $white;
    z-index: 1;
  }

  &__label-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 350px;
    margin-top: 10px;
    padding: 0 5px;
  }

  &__label {
    padding: 8px 12px;
    font-size: 13px;
    color: $white;
    border-radius: 4px;
    color: $white;
    pointer-events: none;
    border-radius: 20px;
    border: 1px solid $fiord;
    background-color: rgba(29, 41, 57, 0.4);

    &--floating {
      position: absolute;
      top: 10px;
      z-index: 2;
    }

    &--before {
      left: 10px;
    }

    &--after {
      right: 10px;
    }

    &--selected {
      border-color: $primary-light;
      background-color: rgba($primary-light, 0.5);
    }
  }

  &__slider-button-icon {
    width: 30%;
    color: $white;
  }

  &__magnifier-container {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid $black;
    position: absolute;
    top: 0;
    width: 350px;
    z-index: 1;

    &--right {
      left: 105%;
    }

    &--left {
      right: 115%;
    }

    &--inline {
      position: static;
      margin-top: 10px;
      width: 100%;

      @include with-tablet {
        width: 350px;
      }
    }

    &--transparent {
      background-image: $transparent-bg;
    }
  }

  &__magnifier-item {
    width: 50%;
    height: 200px;
    background-position:
      v-bind(positionPercentageX) v-bind(positionPercentageY),
      center;
    background-repeat: no-repeat, repeat;
    background-size: 300%, 20%;
    max-height: 150px;
    background-color: v-bind(imageBackground);

    &--before {
      background-image: v-bind(beforeImageCSS);
    }

    &--after {
      background-image: v-bind(afterImageCSS);
    }

    @include with-desktop {
      max-height: 300px;
    }
  }
}
</style>
